(() => {
  'use strict';

  if (window.PWAdvisorAppPriceTypographyV3Loaded) return;
  window.PWAdvisorAppPriceTypographyV3Loaded = true;

  const PAGE_RE = /\/pages\/(?:wasmachine|vaatwasser)-adviseur\/?$/;
  const ROOT_SELECTOR = '[id^="shopify-section-"][id*="advisor_apps"], .cc-apps';
  const CTA_RE = /^bekijk\s+(?:product|wasmachine|vaatwasser)(?:\s*[→›>])?$/i;
  const SPEC_RE = /(?:vulvermogen|toeren|geluidsniveau|fabrieksgarantie|garantie|liter|couverts|energieklasse|energielabel|vriesgedeelte|koelgedeelte|plaats vriesruimte)/i;
  let scheduled = false;

  if (!PAGE_RE.test(window.location.pathname)) return;

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  function textNodesWithin(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  }

  function hasChildWithSameText(element) {
    const text = cleanText(element.textContent);
    return Array.from(element.children).some(
      (child) => cleanText(child.textContent) === text
    );
  }

  function isUsefulTextElement(element) {
    if (!(element instanceof HTMLElement)) return false;
    const text = cleanText(element.textContent);
    if (!text || text.length > 220) return false;
    return !hasChildWithSameText(element);
  }

  function findCtas(root) {
    const matches = new Set();

    root
      .querySelectorAll('a, button, [role="button"], span, p, strong, div')
      .forEach((element) => {
        if (!isUsefulTextElement(element)) return;
        if (!CTA_RE.test(cleanText(element.textContent))) return;
        matches.add(element.closest('a, button, [role="button"]') || element);
      });

    return Array.from(matches);
  }

  function findCard(cta, root) {
    let current = cta.parentElement;

    for (
      let depth = 0;
      current && depth < 12;
      depth += 1, current = current.parentElement
    ) {
      if (!root.contains(current) && current !== root) break;

      const text = cleanText(current.textContent);
      const hasImage = Boolean(current.querySelector('img'));
      const hasPrice = /€\s*\d/.test(text);
      const ctaCount = findCtas(current).length;

      if (hasImage && hasPrice && ctaCount <= 2 && text.length < 1800) {
        return current;
      }

      if (current === root) break;
    }

    return null;
  }

  function appearsBefore(element, reference) {
    return Boolean(
      element.compareDocumentPosition(reference) &
        Node.DOCUMENT_POSITION_FOLLOWING
    );
  }

  function findTitle(card, cta) {
    const candidates = Array.from(
      card.querySelectorAll(
        'h1, h2, h3, h4, h5, h6, [role="heading"], a[href*="/products/"], strong, b, p, span, div'
      )
    ).filter((element) => {
      if (!isUsefulTextElement(element) || !appearsBefore(element, cta)) {
        return false;
      }

      if (element.querySelector('img')) return false;

      const text = cleanText(element.textContent);
      if (text.length < 8 || text.length > 180) return false;
      if (CTA_RE.test(text) || /€\s*\d/.test(text) || SPEC_RE.test(text)) {
        return false;
      }

      return true;
    });

    const candidate = candidates[candidates.length - 1];
    if (!candidate) return null;

    return (
      candidate.closest(
        'h1, h2, h3, h4, h5, h6, [role="heading"], a[href*="/products/"]'
      ) || candidate
    );
  }

  function lowestCommonAncestor(first, second, boundary) {
    const ancestors = new Set();
    let current = first;

    while (current) {
      ancestors.add(current);
      if (current === boundary) break;
      current = current.parentElement;
    }

    current = second;

    while (current) {
      if (ancestors.has(current)) return current;
      if (current === boundary) break;
      current = current.parentElement;
    }

    return null;
  }

  function findPriceHost(card) {
    const nodes = textNodesWithin(card);
    const candidates = [];

    nodes
      .filter((node) => cleanText(node.nodeValue) === '00')
      .forEach((centsNode) => {
        const centsIndex = nodes.indexOf(centsNode);

        for (let index = centsIndex - 1; index >= 0; index -= 1) {
          const euroNode = nodes[index];
          if (!/€\s*\d/.test(euroNode.nodeValue || '')) continue;

          const common = lowestCommonAncestor(
            euroNode.parentElement,
            centsNode.parentElement,
            card
          );

          if (common) {
            const text = cleanText(common.textContent);

            candidates.push({
              element: common,
              paired: true,
              descendants: common.querySelectorAll('*').length,
              length: text.length
            });
          }

          break;
        }
      });

    nodes
      .filter((node) => /€\s*\d/.test(node.nodeValue || ''))
      .forEach((node) => {
        let current = node.parentElement;

        for (
          let depth = 0;
          current && depth < 8;
          depth += 1, current = current.parentElement
        ) {
          const text = cleanText(current.textContent);

          if (text.length <= 120 && /€\s*\d/.test(text)) {
            candidates.push({
              element: current,
              paired: false,
              descendants: current.querySelectorAll('*').length,
              length: text.length
            });
          }

          if (current === card) break;
        }
      });

    candidates.sort((first, second) => {
      if (first.paired !== second.paired) return first.paired ? -1 : 1;
      if (first.descendants !== second.descendants) {
        return first.descendants - second.descendants;
      }
      return first.length - second.length;
    });

    return candidates[0]?.element || null;
  }

  function normaliseThousands(value) {
    return String(value || '').replace(
      /(€\s*)(\d{1,3}(?:,\d{3})+)(?=(?:[.,]\d{2})?(?:\D|$))/g,
      (_match, currency, amount) => `${currency}${amount.replace(/,/g, '.')}`
    );
  }

  function preserveOuterWhitespace(original, replacement) {
    const leading = original.match(/^\s*/)?.[0] || '';
    const trailing = original.match(/\s*$/)?.[0] || '';
    return `${leading}${replacement}${trailing}`;
  }

  function formatPrice(host) {
    if (!(host instanceof HTMLElement)) return;

    host.dataset.pwAdvisorPrice = 'true';
    host.dataset.pwFinalPrice = 'true';

    let nodes = textNodesWithin(host);

    nodes.forEach((node) => {
      const current = node.nodeValue || '';
      const formatted = normaliseThousands(current).replace(
        /([,.])00(?=\s*(?:[A-Z]{3})?(?:$|[^\d]))/g,
        ',-'
      );

      if (formatted !== current) node.nodeValue = formatted;
    });

    nodes = textNodesWithin(host);

    nodes
      .filter((node) => cleanText(node.nodeValue) === '00')
      .forEach((node) => {
        const ordered = textNodesWithin(host);
        const index = ordered.indexOf(node);
        const before = ordered
          .slice(0, index)
          .map((item) => item.nodeValue || '')
          .join('');

        if (!/€\s*\d/.test(before)) return;

        for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
          const candidate = ordered[cursor];
          if (!/€\s*\d/.test(candidate.nodeValue || '')) continue;

          const formatted = normaliseThousands(candidate.nodeValue || '');
          if (formatted !== candidate.nodeValue) candidate.nodeValue = formatted;
          break;
        }

        node.nodeValue = preserveOuterWhitespace(node.nodeValue || '', ',-');

        if (node.parentElement instanceof HTMLElement) {
          node.parentElement.dataset.pwAdvisorCents = 'true';
          node.parentElement.dataset.pwPriceCents = 'true';
        }
      });
  }

  function markCardCopy(card, title, priceHost, cta) {
    card.dataset.pwAdvisorCard = 'true';

    if (title instanceof HTMLElement) title.dataset.pwAdvisorTitle = 'true';
    if (cta instanceof HTMLElement) cta.dataset.pwAdvisorCta = 'true';
    if (priceHost instanceof HTMLElement) {
      priceHost.dataset.pwAdvisorPrice = 'true';
      priceHost.dataset.pwFinalPrice = 'true';
    }

    card.querySelectorAll('p, li, span, div, strong').forEach((element) => {
      if (!isUsefulTextElement(element)) return;
      if (title && (element === title || title.contains(element))) return;
      if (
        priceHost &&
        (element === priceHost || priceHost.contains(element))
      ) {
        return;
      }
      if (cta && (element === cta || cta.contains(element))) return;

      const text = cleanText(element.textContent);
      if (SPEC_RE.test(text)) element.dataset.pwAdvisorSpec = 'true';
    });
  }

  function markGeneralCopy(root) {
    root.dataset.pwWasmachineAdvisor = 'true';

    root.querySelectorAll('h1, h2, h3').forEach((element) => {
      if (!element.closest('[data-pw-advisor-card="true"]')) {
        element.dataset.pwAdvisorHeading = 'true';
      }
    });

    root.querySelectorAll('p, li').forEach((element) => {
      if (
        !element.closest('[data-pw-advisor-card="true"]') &&
        isUsefulTextElement(element)
      ) {
        element.dataset.pwAdvisorCopy = 'true';
      }
    });

    root
      .querySelectorAll('button, label, [role="button"], input, select')
      .forEach((element) => {
        if (!element.closest('[data-pw-advisor-card="true"]')) {
          element.dataset.pwAdvisorControl = 'true';
        }
      });
  }

  function enhanceRoot(root) {
    markGeneralCopy(root);

    findCtas(root).forEach((cta) => {
      const card = findCard(cta, root);
      if (!card) return;

      const title = findTitle(card, cta);
      const priceHost = findPriceHost(card);

      if (priceHost) formatPrice(priceHost);
      markCardCopy(card, title, priceHost, cta);
    });
  }

  function enhance() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement && root.querySelector('img')) {
        enhanceRoot(root);
      }
    });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;

    window.requestAnimationFrame(() => {
      scheduled = false;
      enhance();
    });
  }

  function init() {
    enhance();

    new MutationObserver((records) => {
      const relevant = records.some(
        (record) =>
          record.type === 'characterData' ||
          Array.from(record.addedNodes).some(
            (node) => node.nodeType === Node.ELEMENT_NODE
          )
      );

      if (relevant) schedule();
    }).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    window.addEventListener('pageshow', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
